import { Controller } from "@hotwired/stimulus"
import * as XLSX from "xlsx"

// Counts the rows of the picked file in the browser and hands the number to the
// upload modal's Alpine scope. The ceiling itself is not decided here: the modal
// holds the one the server rendered, so it and the wizard can never disagree
// about how big a file is allowed to be.
export default class extends Controller {
    async countRows(event) {
        const file = event.target.files[0]
        if (!file) return

        // The modal's state lives in the x-data on this controller's element.
        const state = window.Alpine && Alpine.$data(this.element)
        const ext = file.name.split(".").pop().toLowerCase()

        try {
            let lines

            if (ext === "csv") {
                lines = await this.countCsvRows(file)
            } else if (ext === "xlsx" || ext === "xls") {
                lines = await this.countXlsxRows(file)
            } else {
                throw new Error("Unsupported file type")
            }

            // The first line is the header - the server counts rows the same way
            // (PreviewFile: data.count - 1), and a file used to be refused here
            // one row before the wizard refused it.
            const rows = Math.max(0, lines - 1)
            if (rows === 0) throw new Error("Empty file")

            if (state) state.counted(rows)
        } catch (err) {
            if (state) state.unreadable()
        }
    }

    countCsvRows(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()

            reader.onload = (e) => {
                const text = e.target.result
                const rows = text
                    .split(/\r\n|\n/)
                    .filter(line => line.trim() !== "")

                resolve(rows.length)
            }

            reader.onerror = () => reject(new Error("Failed to read CSV"))
            reader.readAsText(file)
        })
    }

    countXlsxRows(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()

            reader.onload = (e) => {
                const data = new Uint8Array(e.target.result)
                const workbook = XLSX.read(data, { type: "array" })

                const sheet = workbook.Sheets[workbook.SheetNames[0]]
                const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 })

                resolve(rows.filter(r => r.length).length)
            }

            reader.onerror = () => reject(new Error("Failed to read XLSX"))
            reader.readAsArrayBuffer(file)
        })
    }
}
